import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { Stack } from '@mui/system';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import {
  useCouponByIdRelayQuery,
  mapCouponToICoupon,
  useUserCouponsByCoupon,
  type UserCouponGraphQL,
  type UserCouponStatusFilter,
} from '../../hooks/coupons/query/V2';
import { useRevokeUserCoupon } from '../../hooks/coupons/mutation/V2';
import { ICoupon } from '../../ts/interface/coupon.interface';
import { IAuth } from '../../ts/interface/admins.interface';
import { findAccess, getLocaleDateTimeString } from '../../utils';
import useOrganization from '../../hooks/use-organization';
import PageContainer from '../Redesing/page-container';
import PageContent from '../Redesing/page-content';
import CardView from '../Redesing/card-view';
import DetailPageSkeleton from '../Redesing/skeleton/DetailPageSkeleton';
import { SeverityPill } from '../Redesing/severity-pill';
import IconButtonMenu from '../Redesing/icon-button-menu';
import CustomAsyncToast from '../Redesing/custom-async-toast';
import AssignCouponToUser from './AssignCouponToUser';

interface CouponAssignedUsersProps {
  scope: 'system' | 'organization';
  systemLoaded: boolean;
  systemAccess: number;
  movaticAccess: number;
}

function userDisplay(grant: UserCouponGraphQL): string {
  const user = grant.user;
  if (!user) return grant.userOldId;
  const name =
    user.fullName || [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return name || user.email || user.phone || grant.userOldId;
}

const CouponAssignedUsers = ({
  scope,
  systemLoaded,
  systemAccess,
  movaticAccess,
}: CouponAssignedUsersProps) => {
  const { couponId } = useParams<{ couponId: string }>();
  const organization = useOrganization();

  const isOrg = scope === 'organization';
  const detailHref = isOrg
    ? `/organization/billing/coupon/${couponId}`
    : `/billing/coupon/${couponId}`;

  const accessLevel = isOrg ? organization?.access ?? systemAccess : systemAccess;
  const canManage = findAccess(accessLevel).includes('rates') || Boolean(movaticAccess);

  const { data: couponData, isLoading: couponLoading } = useCouponByIdRelayQuery(
    couponId,
    { enabled: isOrg ? Boolean(couponId) : systemLoaded }
  );
  const coupon = useMemo(
    () => (couponData ? mapCouponToICoupon(couponData) : ({} as ICoupon)),
    [couponData]
  );

  const couponOldId = couponData?.oldId ?? '';

  const [statusFilter, setStatusFilter] = useState<UserCouponStatusFilter>('ACTIVE');
  const [showAssignModal, setShowAssignModal] = useState(false);

  const {
    data: grants,
    isLoading: grantsLoading,
    refetch,
  } = useUserCouponsByCoupon(
    couponOldId ? { couponOldId, status: statusFilter } : undefined,
    { enabled: Boolean(couponOldId) }
  );

  const { revokeUserCoupon } = useRevokeUserCoupon();
  const [revokingId, setRevokingId] = useState<string | null>(null);

  const handleRevoke = (grant: UserCouponGraphQL) => {
    setRevokingId(grant.id);
    CustomAsyncToast({
      loadingMessage: 'Revoking coupon...',
      successMessage: () => 'Coupon revoked successfully!',
      errorMessage: 'There was an error revoking the coupon.',
      promise: () =>
        revokeUserCoupon(grant.id).then((res) => {
          if (!res.isSuccess) {
            throw new Error(res.errors[0]?.message || 'Failed to revoke the coupon.');
          }
        }),
    }).then(() => {
      setRevokingId(null);
      refetch();
    });
  };

  const loading = couponLoading || (!isOrg && !systemLoaded);
  const rows = grants ?? [];

  return (
    <PageContainer>
      {loading ? (
        <DetailPageSkeleton cards={[{ rows: 4, title: true }]} showTable tableRows={3} />
      ) : (
        <PageContent
          backLink={detailHref}
          contentTitle={isOrg ? 'Organization Coupons' : 'Coupons'}
          itemName={`${coupon.name || coupon.code || 'Coupon'} assignments`}
          itemChipValue={coupon.gid || coupon.id || ''}
          chipLabel={'Coupon ID'}
          itemTopButtons={
            <IconButtonMenu
              buttons={[
                {
                  label: 'Assign to user',
                  startIcon: <AddIcon />,
                  disabled: !canManage,
                  onClick: () => setShowAssignModal(true),
                },
              ]}
            />
          }
          tabsChildren={
            <Stack spacing={3}>
              <CardView
                title={'Assigned users'}
                isLoading={grantsLoading}
                headerActions={
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={statusFilter}
                    onChange={(_e, next: UserCouponStatusFilter | null) => {
                      if (next) setStatusFilter(next);
                    }}
                    aria-label="Filter assignments by status"
                    data-id="coupon-assignments-status-filter"
                  >
                    <ToggleButton value="ACTIVE" aria-label="Active assignments">
                      Active
                    </ToggleButton>
                    <ToggleButton value="REVOKED" aria-label="Revoked assignments">
                      Revoked
                    </ToggleButton>
                    <ToggleButton value="ALL" aria-label="All assignments">
                      All
                    </ToggleButton>
                  </ToggleButtonGroup>
                }
                content={
                  rows.length === 0 ? (
                    <Box sx={{ p: 3 }}>
                      <Typography color="text.secondary" variant="body1">
                        {statusFilter === 'REVOKED'
                          ? 'This coupon has no revoked assignments.'
                          : statusFilter === 'ACTIVE'
                          ? 'This coupon has no active assignments.'
                          : 'This coupon has not been assigned to any users.'}
                      </Typography>
                    </Box>
                  ) : (
                    <Table data-id="coupon-assignments-table">
                      <TableHead>
                        <TableRow>
                          <TableCell>User</TableCell>
                          <TableCell>Assigned by</TableCell>
                          <TableCell>Assigned on</TableCell>
                          <TableCell>Times used</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((grant) => (
                          <TableRow key={grant.id} hover>
                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography variant="subtitle2">
                                  {userDisplay(grant)}
                                </Typography>
                                <Typography color="text.secondary" variant="body2">
                                  {grant.user?.phone || grant.userOldId}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>{grant.assignedBy || '—'}</TableCell>
                            <TableCell>
                              {grant.created
                                ? getLocaleDateTimeString(grant.created)
                                : '—'}
                            </TableCell>
                            <TableCell>{grant.timesUsed ?? 0}</TableCell>
                            <TableCell>
                              <SeverityPill color={grant.removedOn ? 'error' : 'success'}>
                                {grant.removedOn ? 'Revoked' : 'Active'}
                              </SeverityPill>
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                data-id={`coupon-revoke-${grant.id}`}
                                color="error"
                                size="small"
                                disabled={
                                  !canManage ||
                                  Boolean(grant.removedOn) ||
                                  revokingId === grant.id
                                }
                                onClick={() => handleRevoke(grant)}
                              >
                                Revoke
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )
                }
              />
            </Stack>
          }
        />
      )}
      {showAssignModal && (
        <AssignCouponToUser
          scope={scope}
          open={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          onAssigned={() => {
            setShowAssignModal(false);
            refetch();
          }}
          couponOldId={couponOldId}
          couponName={coupon.name || coupon.code || 'Coupon'}
          systemAccess={systemAccess}
          movaticAccess={movaticAccess}
        />
      )}
    </PageContainer>
  );
};

const mapState = (state: {
  system: { isLoaded: boolean };
  admin: { admin: IAuth; systemAccess: number };
}) => ({
  systemLoaded: state.system.isLoaded,
  systemAccess: state.admin.systemAccess,
  movaticAccess: state.admin.admin.admin.movaticAccess,
});

export const CouponAssignedUsersSystem = connect(mapState)(
  (props: Omit<CouponAssignedUsersProps, 'scope'>) => (
    <CouponAssignedUsers {...props} scope="system" />
  )
);

export const CouponAssignedUsersOrganization = connect(mapState)(
  (props: Omit<CouponAssignedUsersProps, 'scope'>) => (
    <CouponAssignedUsers {...props} scope="organization" />
  )
);

export default CouponAssignedUsers;
